import { useCallback } from 'react';

import { useSharedPluginState } from '@atlaskit/editor-common/hooks';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { FindReplacePlugin } from '@atlaskit/editor-plugins/find-replace';
import type { UndoRedoPlugin } from '@atlaskit/editor-plugins/undo-redo';
import { useSharedPluginStateSelector } from '@atlaskit/editor-common/use-shared-plugin-state-selector';

import { fg } from '@confluence/feature-gating';

export const useEditorActions = (
	editorFullPagePresetAPI: PublicPluginAPI<[UndoRedoPlugin, FindReplacePlugin]> | undefined,
): {
	canUndo: boolean;
	canRedo: boolean;
	undo: () => boolean;
	redo: () => boolean;
	activateFindReplace: () => boolean;
} => {
	const { historyState } = useSharedPluginState(editorFullPagePresetAPI, ['history'], {
		disabled: fg('platform_editor_controls_excessive_rerenders'),
	});
	const historyCanUndo = useSharedPluginStateSelector(editorFullPagePresetAPI, 'history.canUndo', {
		disabled: !fg('platform_editor_controls_excessive_rerenders'),
	});
	const historyCanRedo = useSharedPluginStateSelector(editorFullPagePresetAPI, 'history.canRedo', {
		disabled: !fg('platform_editor_controls_excessive_rerenders'),
	});

	const canUndo =
		(editorFullPagePresetAPI?.undoRedoPlugin?.actions.undo &&
			(historyCanUndo || historyState?.canUndo)) ||
		false;
	const canRedo =
		(editorFullPagePresetAPI?.undoRedoPlugin?.actions.redo &&
			(historyCanRedo || historyState?.canRedo)) ||
		false;

	const undo = useCallback(() => {
		if (!editorFullPagePresetAPI?.undoRedoPlugin?.actions) {
			return false;
		}
		return editorFullPagePresetAPI.undoRedoPlugin.actions.undo();
	}, [editorFullPagePresetAPI]);

	const redo = useCallback(() => {
		if (!editorFullPagePresetAPI?.undoRedoPlugin?.actions) {
			return false;
		}
		return editorFullPagePresetAPI.undoRedoPlugin.actions.redo();
	}, [editorFullPagePresetAPI]);

	const activateFindReplace = useCallback(() => {
		if (!editorFullPagePresetAPI?.findReplace?.actions) {
			return false;
		}
		return editorFullPagePresetAPI.findReplace.actions.activateFindReplace();
	}, [editorFullPagePresetAPI]);

	return {
		canUndo,
		canRedo,
		undo,
		redo,
		activateFindReplace,
	};
};
